import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded'
import TuneRoundedIcon from '@mui/icons-material/TuneRounded'
import {
  BACKGROUNDS,
  type BackgroundKey,
  type BlockStyle,
} from '../../components/blocks/blockStyle'

/**
 * Collapsible per-block style controls. Edits a constrained BlockStyle object
 * stored under config.style (fixed choices only — no arbitrary CSS).
 */
export default function BlockStyleFields({
  value,
  onChange,
}: {
  value: BlockStyle
  onChange: (next: BlockStyle) => void
}) {
  const set = (k: keyof BlockStyle, v: string) =>
    onChange({ ...value, [k]: v })

  return (
    <Accordion disableGutters sx={{ boxShadow: 'none', border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
      <AccordionSummary expandIcon={<ExpandMoreRoundedIcon />}>
        <Stack direction="row" spacing={1} alignItems="center">
          <TuneRoundedIcon fontSize="small" color="action" />
          <Typography variant="subtitle2" fontWeight={700}>
            Style
          </Typography>
        </Stack>
      </AccordionSummary>
      <AccordionDetails>
        <Stack spacing={2}>
          <TextField
            select
            size="small"
            label="Background"
            fullWidth
            value={value.background ?? 'none'}
            onChange={(e) => set('background', e.target.value as BackgroundKey)}
          >
            {(Object.keys(BACKGROUNDS) as BackgroundKey[]).map((k) => (
              <MenuItem key={k} value={k}>
                {BACKGROUNDS[k].label}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            size="small"
            label="Text alignment"
            fullWidth
            value={value.align ?? 'left'}
            onChange={(e) => set('align', e.target.value)}
          >
            <MenuItem value="left">Left</MenuItem>
            <MenuItem value="center">Center</MenuItem>
            <MenuItem value="right">Right</MenuItem>
          </TextField>
          <Stack direction="row" spacing={2}>
            <TextField
              select
              size="small"
              label="Padding"
              fullWidth
              value={value.padding ?? 'none'}
              onChange={(e) => set('padding', e.target.value)}
            >
              <MenuItem value="none">None</MenuItem>
              <MenuItem value="sm">Small</MenuItem>
              <MenuItem value="md">Medium</MenuItem>
              <MenuItem value="lg">Large</MenuItem>
            </TextField>
            <TextField
              select
              size="small"
              label="Width"
              fullWidth
              value={value.width ?? 'full'}
              onChange={(e) => set('width', e.target.value)}
            >
              <MenuItem value="full">Full width</MenuItem>
              <MenuItem value="contained">Contained</MenuItem>
            </TextField>
          </Stack>
          <Stack direction="row" spacing={2}>
            <TextField
              select
              size="small"
              label="Space above"
              fullWidth
              value={value.marginTop ?? 'none'}
              onChange={(e) => set('marginTop', e.target.value)}
            >
              <MenuItem value="none">None</MenuItem>
              <MenuItem value="sm">Small</MenuItem>
              <MenuItem value="md">Medium</MenuItem>
              <MenuItem value="lg">Large</MenuItem>
            </TextField>
            <TextField
              select
              size="small"
              label="Space below"
              fullWidth
              value={value.marginBottom ?? 'none'}
              onChange={(e) => set('marginBottom', e.target.value)}
            >
              <MenuItem value="none">None</MenuItem>
              <MenuItem value="sm">Small</MenuItem>
              <MenuItem value="md">Medium</MenuItem>
              <MenuItem value="lg">Large</MenuItem>
            </TextField>
          </Stack>
        </Stack>
      </AccordionDetails>
    </Accordion>
  )
}
